import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { MeetingRepository } from 'repository/meetingRepository';
import { SubPlaceModal, SubPlace, SubPlaceForm, getCategoryLabel, getCategoryIcon } from 'components/meeting/SubPlaceModal';
import './TravelMeetingShare.css';

const STORAGE_KEY = 'meeting_create_data';

export const TravelMeetingShare = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showAlert } = useAlert();
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [meetingDetail, setMeetingDetail] = useState<any>(null);

    // 서브 장소 모달 관련 상태
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm | undefined>(undefined);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(false);
    const [subPlaces, setSubPlaces] = useState<SubPlace[]>([]);
    const [locationStateProcessed, setLocationStateProcessed] = useState(false);

    // location.state에서 생성된 모임 데이터 가져오기
    // PlaceSearch에서 돌아올 때도 meetingId를 유지하기 위해 여러 소스에서 가져옴
    const createdMeeting = location.state?.meeting;
    const meetingId = createdMeeting?.meetingId || location.state?.meetingId || sessionStorage.getItem('travel_share_meeting_id');
    const inviteCode = createdMeeting?.inviteCode || location.state?.inviteCode;

    // meetingId를 sessionStorage에 저장 (PlaceSearch에서 돌아올 때 유지하기 위함)
    useEffect(() => {
        if (createdMeeting?.meetingId) {
            sessionStorage.setItem('travel_share_meeting_id', createdMeeting.meetingId);
        }
    }, [createdMeeting?.meetingId]);

    // 여행 기간 계산
    const travelPeriod = useMemo(() => {
        if (!meetingDetail?.travelStartDate || !meetingDetail?.travelEndDate) return null;
        const start = new Date(meetingDetail.travelStartDate);
        const end = new Date(meetingDetail.travelEndDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = days - 1;
        return { days, nights, start, end };
    }, [meetingDetail?.travelStartDate, meetingDetail?.travelEndDate]);

    // 백엔드에서 모임 상세 조회
    const fetchMeetingDetail = useCallback(async () => {
        if (!meetingId) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await MeetingRepository.onGetMeetingDetail(meetingId);
            console.log('모임 상세 조회 응답:', response);
            // API 응답 구조: response.data 또는 response.data.data
            const meetingInfo = response?.data?.data || response?.data;
            if (meetingInfo) {
                console.log('모임 정보:', meetingInfo);
                setMeetingDetail(meetingInfo);
            }
        } catch (error) {
            console.error('모임 상세 조회 실패:', error);
        } finally {
            setIsLoading(false);
        }
    }, [meetingId]);

    // 서브 장소 목록 조회
    const fetchSubPlaces = useCallback(async () => {
        if (!meetingId) return;
        try {
            const response = await MeetingRepository.onGetSubPlaces(meetingId);
            console.log('서브 장소 목록 조회 응답:', response);
            // API 응답 구조: response.data 또는 response.data.data
            const subPlaceList = response?.data?.data || response?.data;
            if (Array.isArray(subPlaceList)) {
                setSubPlaces(subPlaceList);
            } else if (subPlaceList) {
                setSubPlaces([]);
            }
        } catch (error) {
            console.error('서브 장소 목록 조회 실패:', error);
        }
    }, [meetingId]);

    // 초기 로드
    useEffect(() => {
        fetchMeetingDetail();
        fetchSubPlaces();
    }, [fetchMeetingDetail, fetchSubPlaces]);

    // PlaceSearch에서 돌아온 경우 처리 (한 번만 실행)
    useEffect(() => {
        if (location.state?.fromSubPlaceModal && !locationStateProcessed) {
            const savedForm = location.state.subPlaceForm;
            if (location.state?.placeInfo) {
                const placeInfo = location.state.placeInfo;
                setSubPlaceForm({
                    ...(savedForm || { placeName: '', placeAddress: '', category: 'ATTRACTION', dayNumber: 1, visitTime: '', description: '' }),
                    placeName: placeInfo.name || savedForm?.placeName || '',
                    placeAddress: placeInfo.address || savedForm?.placeAddress || ''
                });
            } else if (savedForm) {
                setSubPlaceForm(savedForm);
            }
            setIsCustomVisitTime(location.state.isCustomVisitTime || false);
            if (location.state.editingSubPlaceId) {
                const editingPlace = subPlaces.find(sp => sp.subPlaceId === location.state.editingSubPlaceId);
                if (editingPlace) {
                    setEditingSubPlace(editingPlace);
                }
            }
            setShowSubPlaceModal(true);
            setLocationStateProcessed(true);
        }
    }, [location.state, subPlaces, locationStateProcessed]);

    // 컴포넌트 언마운트 시 sessionStorage 정리
    useEffect(() => {
        return () => {
            sessionStorage.removeItem(STORAGE_KEY);
        };
    }, []);

    // 날짜 포맷팅 함수
    const formatDateRange = (startDate: string, endDate: string) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];

        const startStr = `${start.getMonth() + 1}/${start.getDate()}(${weekdays[start.getDay()]})`;
        const endStr = `${end.getMonth() + 1}/${end.getDate()}(${weekdays[end.getDay()]})`;

        return `${startStr} ~ ${endStr}`;
    };

    // sessionStorage에서 입력한 데이터 가져오기 (fallback용)
    const savedData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

    // 모임 데이터 (백엔드에서 가져온 데이터 우선, 없으면 sessionStorage fallback)
    const meetingData = useMemo(() => {
        // 백엔드 데이터가 있으면 우선 사용
        if (meetingDetail && Object.keys(meetingDetail).length > 0) {
            const dateRange = meetingDetail.travelStartDate && meetingDetail.travelEndDate
                ? formatDateRange(meetingDetail.travelStartDate, meetingDetail.travelEndDate)
                : '';

            // 숙소 정보 (API에서 직접 가져옴)
            const hotelName = meetingDetail.hotelName || meetingDetail.hotel_name || '-';

            return {
                name: meetingDetail.meetingName || '',
                destination: meetingDetail.travelDestination || '',
                destinationEmoji: meetingDetail.travelDestinationInfo?.emoji || '✈️',
                dateRange,
                period: travelPeriod ? `${travelPeriod.nights}박 ${travelPeriod.days}일` : '',
                meetingTime: meetingDetail.meetingTime || '',
                meetingPlace: meetingDetail.placeName || '',
                hotelPlace: hotelName,
                inviteCode: meetingDetail.inviteCode || inviteCode || 'MOI-XXXXXX'
            };
        }

        // 백엔드 데이터가 없으면 sessionStorage fallback
        if (savedData && Object.keys(savedData).length > 0) {
            const dateRange = savedData.travelStartDate && savedData.travelEndDate
                ? formatDateRange(savedData.travelStartDate, savedData.travelEndDate)
                : '';

            const start = savedData.travelStartDate ? new Date(savedData.travelStartDate) : null;
            const end = savedData.travelEndDate ? new Date(savedData.travelEndDate) : null;
            let period = '';
            if (start && end) {
                const diffTime = Math.abs(end.getTime() - start.getTime());
                const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                const nights = days - 1;
                period = `${nights}박 ${days}일`;
            }

            // 숙소 정보 (집결 장소와 동일한 경우 집결 장소 정보 사용)
            const hotelName = savedData.isSameAsMeetingPlace
                ? (savedData.meetingPlace?.name || '-')
                : (savedData.hotelPlace?.name || '-');

            return {
                name: savedData.meetingName || '',
                destination: savedData.destination || '',
                destinationEmoji: savedData.destinationInfo?.emoji || '✈️',
                dateRange,
                period,
                meetingTime: savedData.meetingTime || '',
                meetingPlace: savedData.meetingPlace?.name || '',
                hotelPlace: hotelName,
                inviteCode: inviteCode || 'MOI-XXXXXX'
            };
        }

        return null;
    }, [meetingDetail, travelPeriod, inviteCode, savedData]);

    const handleCopyCode = () => {
        if (meetingData) {
            navigator.clipboard.writeText(meetingData.inviteCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleShareKakao = async () => {
        await showAlert({ message: '카카오톡으로 공유하기' });
    };

    const handleShareLink = async () => {
        if (meetingData) {
            const shareUrl = `https://moi.app/join/${meetingData.inviteCode}`;
            navigator.clipboard.writeText(shareUrl);
            await showAlert({ message: '링크가 복사되었습니다!' });
        }
    };

    // 서브 장소 추가 모달 열기
    const handleOpenSubPlaceModal = () => {
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 수정 모달 열기
    const handleEditSubPlace = (subPlace: SubPlace) => {
        setEditingSubPlace(subPlace);
        setSubPlaceForm(undefined); // SubPlaceModal will populate from editingSubPlace
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    };

    // 서브 장소 모달 닫기
    const handleCloseSubPlaceModal = () => {
        setShowSubPlaceModal(false);
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
    };

    const handleComplete = () => {
        // 페이지 이탈 시 sessionStorage 정리
        sessionStorage.removeItem('travel_share_meeting_id');
        if (meetingId) {
            navigate(`/meeting/detail/${meetingId}`, { replace: true });
        } else {
            navigate('/home', { replace: true });
        }
    };

    const handleSkip = () => {
        // 페이지 이탈 시 sessionStorage 정리
        sessionStorage.removeItem('travel_share_meeting_id');
        if (meetingId) {
            navigate(`/meeting/detail/${meetingId}`, { replace: true });
        } else {
            navigate('/home', { replace: true });
        }
    };

    if (isLoading) {
        return (
            <div className="travel-share-wrapper">
                <Header title="여행 모임 생성 완료" />
                <div className="travel-share-loading">
                    <div className="loading-spinner"></div>
                    <p>모임 정보를 불러오는 중...</p>
                </div>
            </div>
        );
    }

    if (!meetingData) {
        return (
            <div className="travel-share-wrapper">
                <Header title="여행 모임 생성 완료" />
                <div className="travel-share-error">
                    <p>모임 정보를 불러올 수 없습니다.</p>
                    <Button text="홈으로" onPress={() => navigate('/home', { replace: true })} />
                </div>
            </div>
        );
    }

    return (
        <div className="travel-share-wrapper">
            <Header title="여행 모임 생성 완료" />

            <div className="travel-share-content">
                {/* Success Header */}
                <div className="travel-success-header">
                    <div className="success-icon-wrapper">
                        <span className="destination-emoji-large">{meetingData.destinationEmoji}</span>
                        <div className="success-check">✓</div>
                    </div>
                    <h1 className="travel-share-title">{meetingData.destination} 여행 준비 완료!</h1>
                    <p className="travel-share-subtitle">{meetingData.name}</p>
                </div>

                {/* Trip Summary Card */}
                <div className="trip-summary-card">
                    <div className="trip-summary-header">
                        <span className="trip-badge">{meetingData.period}</span>
                    </div>

                    <div className="trip-summary-grid">
                        <div className="trip-info-item">
                            <span className="trip-info-icon">📅</span>
                            <div className="trip-info-content">
                                <span className="trip-info-label">여행 기간</span>
                                <span className="trip-info-value">{meetingData.dateRange}</span>
                            </div>
                        </div>

                        <div className="trip-info-item">
                            <span className="trip-info-icon">⏰</span>
                            <div className="trip-info-content">
                                <span className="trip-info-label">집결 시간</span>
                                <span className="trip-info-value">{meetingData.meetingTime}</span>
                            </div>
                        </div>

                        <div className="trip-info-item">
                            <span className="trip-info-icon">📍</span>
                            <div className="trip-info-content">
                                <span className="trip-info-label">집결 장소</span>
                                <span className="trip-info-value">{meetingData.meetingPlace}</span>
                            </div>
                        </div>

                        <div className="trip-info-item">
                            <span className="trip-info-icon">🏨</span>
                            <div className="trip-info-content">
                                <span className="trip-info-label">숙소</span>
                                <span className="trip-info-value">{meetingData.hotelPlace}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invite Code */}
                <div className="travel-invite-section">
                    <div className="invite-label">초대 코드</div>
                    <div className="invite-code-box">
                        <span className="invite-code-text">{meetingData.inviteCode}</span>
                        <button
                            className={`invite-copy-btn ${copied ? 'copied' : ''}`}
                            onClick={handleCopyCode}
                        >
                            {copied ? '복사됨' : '복사'}
                        </button>
                    </div>
                    <div className="share-quick-buttons">
                        <button className="share-quick-btn kakao" onClick={handleShareKakao}>
                            💬 카카오톡
                        </button>
                        <button className="share-quick-btn link" onClick={handleShareLink}>
                            🔗 링크 복사
                        </button>
                    </div>
                </div>

                {/* Sub Place Section */}
                <div className="travel-subplace-section">
                    <div className="subplace-header">
                        <h3 className="subplace-title">
                            <span className="subplace-icon">📍</span>
                            여행 장소 추가
                        </h3>
                        <p className="subplace-subtitle">
                            방문할 장소를 미리 등록해보세요
                        </p>
                    </div>

                    {/* 추가된 서브 장소 목록 */}
                    {subPlaces.length > 0 && (
                        <div className="added-subplaces">
                            {subPlaces.map((place) => (
                                <div
                                    key={place.subPlaceId}
                                    className="added-subplace-item clickable"
                                    onClick={() => handleEditSubPlace(place)}
                                >
                                    <span className="subplace-item-icon">{getCategoryIcon(place.category)}</span>
                                    <div className="subplace-item-content">
                                        <span className="subplace-item-name">{place.placeName}</span>
                                        <span className="subplace-item-info">
                                            {getCategoryLabel(place.category)}
                                            {place.dayNumber && ` · DAY ${place.dayNumber}`}
                                        </span>
                                    </div>
                                    <span className="subplace-item-arrow">›</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 장소 추가 버튼 */}
                    <button className="add-subplace-btn" onClick={handleOpenSubPlaceModal}>
                        <span className="add-icon">+</span>
                        <span className="add-text">장소 추가하기</span>
                    </button>

                    <div className="subplace-hint">
                        <span className="hint-icon">💡</span>
                        <span className="hint-text">나중에 모임 상세에서도 추가할 수 있어요</span>
                    </div>
                </div>
            </div>

            {/* Footer Buttons */}
            <div className="travel-share-footer">
                <button className="skip-button" onClick={handleSkip}>
                    건너뛰기
                </button>
                <Button text="모임 보기" onPress={handleComplete} />
            </div>

            {/* Sub Place Add/Edit Modal */}
            <SubPlaceModal
                isOpen={showSubPlaceModal}
                onClose={handleCloseSubPlaceModal}
                meetingId={meetingId}
                editingSubPlace={editingSubPlace}
                isTravelMeeting={true}
                travelPeriod={travelPeriod}
                meetingTime={meetingDetail?.meetingTime}
                subPlaces={subPlaces}
                onSaveSuccess={fetchSubPlaces}
                returnTo="/meeting/travel-share"
                initialForm={subPlaceForm}
                initialIsCustomVisitTime={isCustomVisitTime}
            />
        </div>
    );
};
